import { useEffect, useRef, useState } from "react";
import { Microphone, MicrophoneSlash } from "@phosphor-icons/react";

/**
 * Spracheingabe (SPEC §4.11) über die Web Speech API des Browsers. Gesprochenes wird an
 * den Text angehängt und sofort gespeichert — in der Besprechung oder auf der Baustelle
 * hat man selten eine Hand für die Tastatur frei.
 *
 * Datenschutz: Chrome und Edge schicken die Aufnahme zur Erkennung an Google bzw.
 * Microsoft, Safari an Apple. Darum steht es am Knopf, und es gibt keine Daueraufnahme:
 * Ein Tipp startet, ein Tipp (oder eine Pause) beendet. Firefox kann es nicht; dort
 * erscheint kein Knopf.
 */

interface Ergebnis { isFinal: boolean; 0: { transcript: string } }
interface Erkennung {
  lang: string; continuous: boolean; interimResults: boolean;
  onresult: ((e: { resultIndex: number; results: ArrayLike<Ergebnis> }) => void) | null;
  onend: (() => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  start(): void; stop(): void;
}
type ErkennungKlasse = new () => Erkennung;

function klasse(): ErkennungKlasse | null {
  const w = window as unknown as { SpeechRecognition?: ErkennungKlasse; webkitSpeechRecognition?: ErkennungKlasse };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export const diktatMoeglich = () => klasse() !== null;

/** Hängt `neu` an `alt` an: neuer Satz mit Großbuchstaben, Leerzeichen dazwischen. */
export function anhaengen(alt: string, neu: string): string {
  const t = neu.trim();
  if (!t) return alt;
  const satz = t.charAt(0).toUpperCase() + t.slice(1);
  if (!alt.trim()) return satz;
  return /[.!?:]\s*$/.test(alt) ? `${alt.trimEnd()} ${satz}` : `${alt.trimEnd()}. ${satz}`;
}

export function Diktat({ text, speichern, disabled }: {
  text: string; speichern: (v: string) => void; disabled?: boolean;
}) {
  const [an, setAn] = useState(false);
  const [zwischen, setZwischen] = useState("");
  const [fehler, setFehler] = useState("");
  const erk = useRef<Erkennung | null>(null);
  // Der Text kann sich waehrend des Sprechens aendern (anderes Feld gespeichert, Nachladen);
  // angehaengt wird an den jeweils aktuellen Stand.
  const stand = useRef(text);
  stand.current = text;

  useEffect(() => () => erk.current?.stop(), []);
  const K = klasse();
  if (!K) return null;

  const los = () => {
    if (an) { erk.current?.stop(); return; }
    setFehler("");
    const e = new K();
    e.lang = "de-DE";
    e.continuous = true;
    e.interimResults = true;
    e.onresult = (ev) => {
      let fertig = "", vorlaeufig = "";
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const r = ev.results[i]!;
        if (r.isFinal) fertig += r[0].transcript; else vorlaeufig += r[0].transcript;
      }
      setZwischen(vorlaeufig);
      if (fertig.trim()) {
        const neu = anhaengen(stand.current, fertig);
        stand.current = neu;
        speichern(neu);
      }
    };
    e.onerror = (ev) => setFehler(ev.error === "not-allowed" ? "Mikrofon nicht freigegeben." :
                                  ev.error === "no-speech" ? "" : "Spracheingabe abgebrochen.");
    e.onend = () => { setAn(false); setZwischen(""); erk.current = null; };
    erk.current = e;
    e.start();
    setAn(true);
  };

  return (
    <span className="diktat">
      <button type="button" className={`knopf leise symbol${an ? " diktat-an" : ""}`} disabled={disabled}
              aria-pressed={an} aria-label={an ? "Spracheingabe beenden" : "Spracheingabe"}
              title={an ? "Spracheingabe beenden" : "Spracheingabe – die Aufnahme geht zur Erkennung an den Anbieter des Browsers"}
              onClick={los}>
        {an ? <MicrophoneSlash size={16} /> : <Microphone size={16} />}
      </button>
      {an && <span className="mute diktat-zwischen">{zwischen || "hört zu …"}</span>}
      {fehler && <span className="mute">{fehler}</span>}
    </span>
  );
}
